import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Globe, Save, ExternalLink, Eye, EyeOff, Palette, Sparkles } from "lucide-react";

export default function PublicHubSettings() {
  const [ws, setWs] = useState(null);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ handle: "", tagline: "", theme_color: "", cta_label: "", cta_url: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [w, p] = await Promise.all([api.get("/workspaces/current"), api.get("/products")]);
      setWs(w.data);
      setForm({
        handle: w.data?.handle || "",
        tagline: w.data?.tagline || "",
        theme_color: w.data?.theme_color || "",
        cta_label: w.data?.cta_label || "",
        cta_url: w.data?.cta_url || "",
      });
      setProducts(p.data || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!ws) return;
    setBusy(true);
    try {
      await api.patch(`/workspaces/${ws.workspace_id}`, form);
      toast.success("Public Hub saved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
    setBusy(false);
  };

  const toggleVisible = async (p) => {
    try {
      await api.patch(`/products/${p.id}`, { public_visible: !(p.public_visible !== false) });
      await load();
    } catch {
      toast.error("Could not update visibility");
    }
  };

  if (!ws) return <div className="p-10 text-meta">Loading…</div>;

  const handle = form.handle || ws.handle;
  const url = handle ? `${window.location.origin}/${handle}` : null;
  const themeColor = form.theme_color || "#7C3AED";

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1100px] mx-auto space-y-6" data-testid="settings-public-hub">
      <div className="surface p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-xl bg-ai-soft text-ai grid place-items-center"><Globe className="h-4 w-4" /></div>
            <div className="min-w-0">
              <h2 className="font-display text-lg font-bold text-ink">Public Hub</h2>
              <p className="text-xs text-meta">Your single public surface at ashraai.com/{handle || "your-handle"}</p>
            </div>
          </div>
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer" data-testid="hub-preview"
              className="rounded-full ashra-gradient-bg px-4 py-2 text-xs font-semibold text-white inline-flex items-center gap-1 shadow-sm focus-ring">
              Live preview <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          <Field label="Handle" hint="3–32 chars · letters, numbers, - _" value={form.handle} testid="hub-handle"
            placeholder="yourbrand" onChange={(v) => setForm({ ...form, handle: v.toLowerCase() })} prefix={`${window.location.host}/`} />
          <Field label="Tagline" hint="One short line shown under your name" value={form.tagline} testid="hub-tagline"
            placeholder="The system behind my revenue" onChange={(v) => setForm({ ...form, tagline: v })} />
          <Field label="Theme colour" hint="Hex like #7C3AED" value={form.theme_color} testid="hub-color"
            placeholder="#7C3AED" onChange={(v) => setForm({ ...form, theme_color: v })}
            adornment={<span className="h-5 w-5 rounded-full border border-line" style={{ background: themeColor }} />} />
          <Field label="CTA label" hint="Optional primary button text" value={form.cta_label} testid="hub-cta-label"
            placeholder="Work with me" onChange={(v) => setForm({ ...form, cta_label: v })} />
          <div className="sm:col-span-2">
            <Field label="CTA URL" hint="Where the CTA button sends visitors" value={form.cta_url} testid="hub-cta-url"
              placeholder="https://…" onChange={(v) => setForm({ ...form, cta_url: v })} />
          </div>

          <div className="sm:col-span-2 flex items-center gap-2 justify-end pt-2">
            <button type="submit" disabled={busy} data-testid="hub-save"
              className="rounded-full ashra-gradient-bg px-5 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-60 focus-ring">
              <Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>

      <div className="surface p-6">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-ai" />
          <h3 className="font-display text-sm font-bold text-ink">Offers shown on your hub</h3>
        </div>
        <p className="text-xs text-meta mb-4">Toggle which products appear at <span className="font-mono">/{handle || "your-handle"}</span>. Hidden products stay live for direct links.</p>
        <div className="space-y-2">
          {products.length === 0 && <p className="text-sm text-meta">No products yet. Add some in Experiences → Products.</p>}
          {products.map((p) => {
            const visible = p.public_visible !== false;
            return (
              <div key={p.id} className="rounded-xl border border-line p-3 flex items-center gap-3" data-testid={`hub-product-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink text-sm truncate">{p.name}</p>
                  <p className="text-xs text-meta truncate">{p.type} · ${p.price}</p>
                </div>
                <button onClick={() => toggleVisible(p)} data-testid={`hub-toggle-${p.id}`}
                  className={`text-xs font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition focus-ring ${
                    visible ? "bg-ai-soft text-ai hover:bg-violet-100" : "bg-zinc-100 text-meta hover:bg-zinc-200"
                  }`}>
                  {visible ? <><Eye className="h-3 w-3" /> Visible</> : <><EyeOff className="h-3 w-3" /> Hidden</>}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, value, onChange, placeholder, testid, prefix, adornment }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-meta font-bold">{label}</span>
      <div className="mt-1 flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2 focus-within:border-ai transition">
        {prefix && <span className="text-xs text-meta font-mono shrink-0">{prefix}</span>}
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
          className="flex-1 min-w-0 bg-transparent text-sm text-ink outline-none placeholder:text-meta-soft" />
        {adornment}
      </div>
      {hint && <span className="text-[11px] text-meta-soft mt-1 block">{hint}</span>}
    </label>
  );
}
